import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  const textarea = document.createElement('textarea');
  let decoded = text;
  let prev = '';
  let iterations = 0;
  while (decoded !== prev && iterations < 10) {
    prev = decoded;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    iterations++;
  }
  return decoded;
};

const AhriSystemCompare = () => {
  const { getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const selections = useMemo(
    () => (Array.isArray(location.state?.selections) ? location.state.selections : []),
    [location.state?.selections]
  );

  const [productDetails, setProductDetails] = useState({});
  const [productLoading, setProductLoading] = useState(false);
  const [highlightMode, setHighlightMode] = useState('none'); // none | diff | similar | both
  const [columnOrder, setColumnOrder] = useState(() => selections.map((_, idx) => idx));
  const [dragIndex, setDragIndex] = useState(null);

  const orderedSelections = useMemo(
    () => columnOrder.map((idx) => selections[idx]).filter(Boolean),
    [columnOrder, selections]
  );

  const productCodes = useMemo(() => {
    const codes = new Set();
    orderedSelections.forEach((system) => {
      [system?.outdoorProductCode, system?.indoorProductCode, system?.furnaceProductCode]
        .filter(Boolean)
        .forEach((code) => codes.add(code));
    });
    return Array.from(codes);
  }, [orderedSelections]);

  useEffect(() => {
    const loadProductDetails = async () => {
      if (!productCodes.length) return;
      setProductLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/search`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            query: '',
            partnumber: '',
            filters: { productCode: productCodes },
            page: 1,
            page_size: Math.min(Math.max(productCodes.length, 1), 100),
            context: 'system_shop_compare',
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `Unable to load product details (HTTP ${resp.status})`);
        }
        const byCode = {};
        (data.docs || []).forEach((doc) => {
          const code = String(doc.productCode || doc.product_code || '').trim();
          if (code) {
            byCode[code] = doc;
          }
        });
        setProductDetails(byCode);
      } catch (err) {
        console.error('Failed to load product details', err);
      } finally {
        setProductLoading(false);
      }
    };
    loadProductDetails();
  }, [productCodes, getAuthHeaders]);

  const parseAttributeString = React.useCallback((raw) => {
    const out = {};
    let text = String(raw || '').trim();
    if (!text) return out;
    text = text.replace(/^[{[\s]+|[}\]\s]+$/g, '').trim();

    // Handle quoted key/value pairs, even if ":" is missing
    const quotedPairRegex = /"([^"]+)"\s*:?\s*"([^"]*)"/g;
    let match = quotedPairRegex.exec(text);
    while (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      if (key) out[key] = value;
      match = quotedPairRegex.exec(text);
    }
    if (Object.keys(out).length > 0) {
      return out;
    }

    text
      .split(/[;,]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const idx = part.indexOf(':');
        if (idx === -1) return;
        const key = part.slice(0, idx).replace(/^"+|"+$/g, '').trim();
        const value = part.slice(idx + 1).replace(/^"+|"+$/g, '').trim();
        if (!key) return;
        out[key] = value;
      });
    return out;
  }, []);

  const getProductAttributes = React.useCallback((detail) => {
    if (!detail || typeof detail !== 'object') return {};
    const candidates = [
      detail.attributes,
      detail.attributes_json,
      detail.attributesJson,
      detail.product_attributes,
      detail.productAttributes,
      detail.attribute_map,
      detail.attributeMap,
    ];
    for (const entry of candidates) {
      if (!entry) continue;
      if (typeof entry === 'string') {
        try {
          const parsed = JSON.parse(entry);
          if (parsed && typeof parsed === 'object') return parsed;
        } catch {
          const parsed = parseAttributeString(entry);
          if (Object.keys(parsed).length > 0) return parsed;
          continue;
        }
      }
      if (typeof entry === 'object') {
        return entry;
      }
    }
    return {};
  }, [parseAttributeString]);


  const formatAttributeValue = React.useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      const parts = value
        .map((item) => formatAttributeValue(item))
        .filter((item) => String(item || '').trim() !== '');
      return parts.join(', ');
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed);
          return formatAttributeValue(parsed);
        } catch {
          return trimmed;
        }
      }
      if (trimmed.includes(':') && trimmed.includes(';')) {
        const parsed = parseAttributeString(trimmed);
        if (Object.keys(parsed).length > 0) {
          return Object.entries(parsed)
            .map(([k, v]) => `${k}: ${formatAttributeValue(v)}`)
            .join('; ');
        }
      }
      return trimmed;
    }
    if (typeof value === 'object') {
      const entries = Object.entries(value).map(
        ([k, v]) => `${k}: ${formatAttributeValue(v)}`
      );
      return entries.filter((entry) => entry.trim() !== '').join('; ');
    }
    return String(value);
  }, [parseAttributeString]);

  const buildAttributeEntries = React.useCallback((attrs) => {
    const rows = [];
    const source = attrs && typeof attrs === 'object' ? attrs : {};
    Object.entries(source).forEach(([key, value]) => {
      if (value === null || value === undefined || String(value).trim() === '') return;
      if (typeof value === 'string') {
        const parsed = parseAttributeString(value);
        if (Object.keys(parsed).length > 0) {
          Object.entries(parsed).forEach(([subKey, subVal]) => {
            const formatted = formatAttributeValue(subVal);
            if (formatted && formatted.trim() !== '') {
              rows.push({ key: subKey, value: formatted });
            }
          });
          return;
        }
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([subKey, subVal]) => {
          const formatted = formatAttributeValue(subVal);
          if (formatted && formatted.trim() !== '') {
            rows.push({ key: subKey, value: formatted });
          }
        });
        return;
      }
      const formatted = formatAttributeValue(value);
      if (formatted && formatted.trim() !== '') {
        const parsed = parseAttributeString(formatted);
        if (Object.keys(parsed).length > 0) {
          Object.entries(parsed).forEach(([subKey, subVal]) => {
            const formattedSub = formatAttributeValue(subVal);
            if (formattedSub && formattedSub.trim() !== '') {
              rows.push({ key: subKey, value: formattedSub });
            }
          });
        } else {
          rows.push({ key, value: formatted });
        }
      }
    });
    return rows;
  }, [formatAttributeValue, parseAttributeString]);

  const attributeRows = useMemo(() => {
    const buildRows = (label, getCode) => {
      const keys = new Set();
      orderedSelections.forEach((system) => {
        const code = getCode(system);
        const attrs = getProductAttributes(productDetails[code]);
        if (attrs && typeof attrs === 'object') {
          const entries = buildAttributeEntries(attrs);
          entries.forEach((entry) => keys.add(entry.key));
        }
      });
      return Array.from(keys).map((key) => ({
        key: `${label}:${key}`,
        label: `${label} - ${key}`,
        value: (system) => {
          const code = getCode(system);
          const attrs = getProductAttributes(productDetails[code]) || {};
          const entries = buildAttributeEntries(attrs);
          const hit = entries.find((entry) => entry.key === key);
          return hit?.value || '-';
        },
      }));
    };

    return {
      outdoor: buildRows('Outdoor', (s) => s?.outdoorProductCode),
      indoor: buildRows('Indoor', (s) => s?.indoorProductCode),
      furnace: buildRows('Furnace', (s) => s?.furnaceProductCode),
    };
  }, [orderedSelections, productDetails, buildAttributeEntries, getProductAttributes]);

  const compareRows = useMemo(
    () => [
      { key: 'product:outdoor', label: 'Outdoor Unit', type: 'product', productType: 'outdoor' },
      ...(attributeRows.outdoor || []),
      { key: 'product:indoor', label: 'Indoor Unit', type: 'product', productType: 'indoor' },
      ...(attributeRows.indoor || []),
      { key: 'product:furnace', label: 'Furnace', type: 'product', productType: 'furnace' },
      ...(attributeRows.furnace || []),
      { key: 'seer2', label: 'SEER2', value: (s) => s.seer2 || '-' },
      { key: 'hspf2', label: 'HSPF2', value: (s) => s.hspf2 || '-' },
      { key: 'eer2', label: 'EER2', value: (s) => s.eer2 || '-' },
      { key: 'coolingCapacity', label: 'Cooling Capacity', value: (s) => s.coolingCapacity || '-' },
      { key: 'program', label: 'Program', value: (s) => s.programName || '-' },
      { key: 'region', label: 'Region', value: (s) => s.region || '-' },
      { key: 'energyStar', label: 'Energy Star', value: (s) => (s.isEnergyStar ? 'Yes' : 'No') },
      { key: 'completeMatch', label: 'Complete Match', value: (s) => (s.completeMatch ? 'Yes' : 'No') },
      { key: 'partialMatch', label: 'Partial Match', value: (s) => (s.partialMatch ? 'Yes' : 'No') },
      { key: 'productCodes', label: 'Product Codes', value: (s) =>
        [s.outdoorProductCode, s.indoorProductCode, s.furnaceProductCode].filter(Boolean).join(', ') || '-' },
    ],
    [attributeRows]
  );


  const renderProductCard = (code, label) => {
    if (!code) return null;
    const detail = productDetails[code] || {};
    const imageSrc = detail.image_url || detail.imageUrl || detail.image || '';
    return (
      <div key={`${label}-${code}`} className="compare-product-card">
        <div className="compare-product-label">{label}</div>
        <div className="compare-product-body">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={`${label}: ${detail.name || code}`}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="compare-product-placeholder">No Image</div>
          )}
          <div className="compare-product-info">
            <div className="compare-product-name">
              {decodeHtmlEntities(detail.name) || code}
            </div>
            <div className="compare-product-meta">
              {detail.manufacturer_part_number || detail.manufacturerPartNumber || code}
            </div>
            {detail.category && (
              <div className="compare-product-meta">{detail.category}</div>
            )}
            {detail.description && (
              <div className="compare-product-desc">{detail.description}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const normalizeCompareValue = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.map((v) => String(v ?? '')).join(',').toLowerCase();
    return String(value).trim().toLowerCase();
  };

  const getProductHighlightClass = (productType, system) => {
    if (highlightMode === 'none') return '';
    const getCode = (sys) =>
      productType === 'outdoor'
        ? sys.outdoorProductCode
        : productType === 'indoor'
        ? sys.indoorProductCode
        : sys.furnaceProductCode;
    const values = orderedSelections.map((s) => normalizeCompareValue(getCode(s)));
    const unique = new Set(values.filter((v) => v !== '' && v !== '-'));
    if (unique.size === 0) return '';
    const allSame = unique.size === 1;
    if ((highlightMode === 'similar' || highlightMode === 'both') && allSame) return 'compare-highlight-similar';
    if ((highlightMode === 'diff' || highlightMode === 'both') && !allSame) return 'compare-highlight-diff';
    return '';
  };

  const getRowCellHighlightClass = (row, system) => {
    if (row.type === 'products' || row.type === 'section') return '';
    if (highlightMode === 'none') return '';
    if (typeof row.value !== 'function') return '';
    const values = orderedSelections.map((s) => normalizeCompareValue(row.value(s)));
    const unique = new Set(values.filter((v) => v !== '' && v !== '-'));
    if (unique.size === 0) return '';
    const allSame = unique.size === 1;
    if (highlightMode === 'similar') {
      return allSame ? 'compare-highlight-similar' : '';
    }
    if (highlightMode === 'diff') {
      return allSame ? '' : 'compare-highlight-diff';
    }
    // highlightMode === 'both'
    if (allSame) return 'compare-highlight-similar';
    const target = normalizeCompareValue(row.value(system));
    const first = values[0];
    return target === first ? 'compare-highlight-similar' : 'compare-highlight-diff';
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1);
      return;
    }
    navigate('/ahri/shop');
  };

  return (
    <div className="container-fluid">
      <div className="compare-hero mb-4">
        <div className="compare-hero__content">
          <div>
            <Title level={3} className="mb-1">Compare AHRI Systems</Title>
            <Text>Review AHRI data and matched products side-by-side.</Text>
          </div>
        <div className="compare-hero__actions">
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>Back</Button>
            <Tag color="blue">{selections.length} Selected</Tag>
            <div className="compare-hero__toggles">
              <Button
                size="small"
                type={highlightMode === 'diff' ? 'primary' : 'default'}
                onClick={() => setHighlightMode('diff')}
              >
                Highlight Differences
              </Button>
              <Button
                size="small"
                type={highlightMode === 'similar' ? 'primary' : 'default'}
                onClick={() => setHighlightMode('similar')}
              >
                Highlight Similar
              </Button>
              <Button
                size="small"
                type={highlightMode === 'both' ? 'primary' : 'default'}
                onClick={() => setHighlightMode('both')}
              >
                Highlight Both
              </Button>
              <Button size="small" onClick={() => setHighlightMode('none')}>
                Clear
              </Button>
            </div>
          </div>
        </div>
      </div>

      {selections.length < 2 ? (
        <Card>
          <Empty description="Select at least 2 systems to compare." />
          <div className="mt-3 text-center">
            <Button type="primary" onClick={handleBack}>Back to System Shop</Button>
          </div>
        </Card>
      ) : (
        <>
          {productLoading && (
            <div className="text-center py-3">
              <Spin />
              <div className="text-muted mt-2">Loading product details…</div>
            </div>
          )}

          <div className="d-none d-lg-block">
            <div className="compare-table">
              <div
                className="compare-row-grid compare-row-header"
                style={{ gridTemplateColumns: `200px repeat(${selections.length}, minmax(260px, 1fr))` }}
              >
                <div className="compare-cell compare-header compare-label">Attribute</div>
                {orderedSelections.map((system, index) => (
                  <div
                    key={`header-${system.ahriId || system.outdoorProductCode}`}
                    className={`compare-cell compare-header ${dragIndex === index ? 'compare-col-dragging' : ''}`}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragEnd={() => setDragIndex(null)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (dragIndex === null || dragIndex === index) return;
                      setColumnOrder((prev) => {
                        const next = [...prev];
                        const from = dragIndex;
                        const [moved] = next.splice(from, 1);
                        next.splice(index, 0, moved);
                        return next;
                      });
                      setDragIndex(null);
                    }}
                  >
                    <div className="fw-semibold">AHRI #{system.ahriId || '-'}</div>
                    <div className="text-muted small">
                      {(system.outdoorBrand || '-')}{' / '}{(system.indoorBrand || '-')}
                    </div>
                    <div className="compare-col-controls">
                      <button
                        type="button"
                        className="compare-col-btn"
                        disabled={index === 0}
                        onClick={() =>
                          setColumnOrder((prev) => {
                            const next = [...prev];
                            const swapIdx = index - 1;
                            [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
                            return next;
                          })
                        }
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        className="compare-col-btn"
                        disabled={index === orderedSelections.length - 1}
                        onClick={() =>
                          setColumnOrder((prev) => {
                            const next = [...prev];
                            const swapIdx = index + 1;
                            [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
                            return next;
                          })
                        }
                      >
                        →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {compareRows.map((row) => (
                <div
                  key={`row-${row.key}`}
                  className="compare-row-grid"
                  style={{ gridTemplateColumns: `200px repeat(${selections.length}, minmax(260px, 1fr))` }}
                >
                  <div className="compare-cell compare-label">
                    <div className="compare-label-row">
                      <span>{row.label}</span>
                    </div>
                  </div>
                  {orderedSelections.map((system) => (
                    <div
                      key={`${system.ahriId || system.outdoorProductCode}-${row.key}`}
                      className={`compare-cell ${getRowCellHighlightClass(row, system)}`}
                    >
                      {row.type === 'product' ? (
                        <div className={`compare-product-highlight ${getProductHighlightClass(row.productType, system)}`}>
                          {row.productType === 'outdoor'
                            ? renderProductCard(system.outdoorProductCode, 'Outdoor Unit')
                            : row.productType === 'indoor'
                            ? renderProductCard(system.indoorProductCode, 'Indoor Unit')
                            : renderProductCard(system.furnaceProductCode, 'Furnace')}
                        </div>
                      ) : (
                        row.value(system)
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="d-block d-lg-none">
            {orderedSelections.map((system) => (
              <Card key={`mobile-${system.ahriId}`} className="compare-system-card mb-3">
                <div className="mb-2">
                  <div className="fw-semibold">AHRI #{system.ahriId || '-'}</div>
                  <div className="text-muted small">
                    {(system.outdoorBrand || '-')}{' / '}{(system.indoorBrand || '-')}
                  </div>
                </div>
                <div className="compare-product-stack">
                  <div className={`compare-product-highlight ${getProductHighlightClass('outdoor', system)}`}>
                    {renderProductCard(system.outdoorProductCode, 'Outdoor Unit')}
                  </div>
                  <div className={`compare-product-highlight ${getProductHighlightClass('indoor', system)}`}>
                    {renderProductCard(system.indoorProductCode, 'Indoor Unit')}
                  </div>
                  <div className={`compare-product-highlight ${getProductHighlightClass('furnace', system)}`}>
                    {renderProductCard(system.furnaceProductCode, 'Furnace')}
                  </div>
                </div>
                {compareRows.filter((row) => row.type !== 'product').map((row) => (
                  <div key={`mobile-${system.ahriId}-${row.key}`} className={`compare-row ${getRowCellHighlightClass(row, system)}`}>
                    <div className="compare-label">{row.label}</div>
                    <div className="compare-value">
                      {typeof row.value === 'function' ? row.value(system) : '-'}
                    </div>
                  </div>
                ))}
              </Card>
            ))}
          </div>
        </>
      )}

      <style>{`
        .compare-hero {
          background: linear-gradient(120deg, #f8fafc 0%, #ffffff 60%);
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 18px 20px;
        }
        .compare-hero__content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .compare-hero__content h3 {
          color: #0f172a;
        }
        .compare-hero__content .ant-typography {
          color: #475569;
        }
        .compare-hero__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        .compare-hero__toggles {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        @media (min-width: 992px) {
          .compare-hero__content {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
        }
        .compare-table {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          overflow: hidden;
          background: #ffffff;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
        }
        .compare-row-grid {
          display: grid;
          border-bottom: 1px solid #e2e8f0;
        }
        .compare-row-grid:last-child {
          border-bottom: none;
        }
        .compare-cell {
          padding: 12px 14px;
          border-right: 1px solid #e2e8f0;
          font-size: 13px;
          color: #1f2937;
          min-height: 52px;
          display: flex;
          align-items: flex-start;
        }
        .compare-cell:last-child {
          border-right: none;
        }
        .compare-header {
          background: #f9fafb;
          min-height: 88px;
          align-items: flex-start;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }
        .compare-col-controls {
          display: flex;
          gap: 6px;
          margin-top: 6px;
        }
        .compare-header {
          cursor: grab;
        }
        .compare-col-dragging {
          opacity: 0.6;
          border: 1px dashed #94a3b8;
        }
        .compare-col-btn {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          color: #1f2937;
          font-size: 12px;
          border-radius: 6px;
          padding: 2px 6px;
          cursor: pointer;
        }
        .compare-col-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .compare-label {
          background: #f3f4f6;
          font-weight: 600;
          color: #334155;
        }
        .compare-row-header {
          border-bottom: 1px solid #e2e8f0;
        }
        .compare-product-stack {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }
        .compare-product-card {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 12px;
          background: #ffffff;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
        }
        .compare-product-label {
          font-size: 12px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .compare-product-body {
          display: grid;
          grid-template-columns: 64px 1fr;
          gap: 10px;
          align-items: center;
        }
        .compare-product-body img {
          width: 64px;
          height: 64px;
          object-fit: contain;
          border-radius: 8px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
        }
        .compare-product-placeholder {
          width: 64px;
          height: 64px;
          border-radius: 8px;
          background: #e2e8f0;
          color: #64748b;
          font-size: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 4px;
        }
        .compare-product-name {
          font-weight: 600;
          color: #0f172a;
          font-size: 13px;
          line-height: 1.3;
        }
        .compare-product-meta {
          font-size: 12px;
          color: #475569;
          margin-top: 2px;
        }
        .compare-product-desc {
          font-size: 11px;
          color: #64748b;
          margin-top: 4px;
          max-height: 34px;
          overflow: hidden;
        }
        .compare-label-row {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .compare-system-card {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
        }
        .compare-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          padding: 8px 0;
          border-bottom: 1px solid #f1f5f9;
          font-size: 13px;
        }
        .compare-product-highlight.compare-highlight-diff {
          border-radius: 12px;
          padding: 6px;
          background: #fee2e2;
        }
        .compare-product-highlight.compare-highlight-similar {
          border-radius: 12px;
          padding: 6px;
          background: #d1fae5;
        }
        .compare-highlight-diff {
          background: #ffe4e6;
        }
        .compare-highlight-similar {
          background: #dcfce7;
        }
        .compare-row:last-child {
          border-bottom: none;
        }
        .compare-row .compare-label {
          color: #475569;
          font-weight: 600;
        }
        .compare-row .compare-value {
          color: #111827;
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default AhriSystemCompare;
